import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/admin.service';
import Badge from '../../components/common/Badge';

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [showPassMap, setShowPassMap] = useState<Record<number, boolean>>({});
  const [passwordsCache, setPasswordsCache] = useState<Record<number, string>>({});
  
  // Frontend Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchUsers = async () => {
    try {
      const res = await adminService.getUsers();
      // Ensure we extract the array from the standard API response structure
      const data = Array.isArray(res) ? res : (res?.data || []);
      setUsers(data);
    } catch (err) {
      console.error("Node Sync Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleTogglePassword = async (userId: number) => {
    if (!userId) {
      console.error("Security Alert: Invalid User Node Identifier");
      return;
    }

    const isCurrentlyVisible = showPassMap[userId];
    
    // If opening and not cached, perform hardware-accelerated decryption (API fetch)
    if (!isCurrentlyVisible && !passwordsCache[userId]) {
      setProcessing(userId);
      try {
        const response = await adminService.getUserPassword(userId);
        if (response && response.status && response.data) {
          // Success: Update the local security cache with the decrypted password
          setPasswordsCache(prev => ({ ...prev, [userId]: response.data.password }));
        } else {
          console.error("Protocol Error:", response?.message || "Invalid Security Response");
        }
      } catch (err: any) {
        console.error("Protocol Error: Failed to retrieve security key", err.message);
        alert(`SECURITY BREACH: ${err.message || 'Unable to fetch password'}`);
      } finally {
        setProcessing(null);
      }
    }
    
    setShowPassMap(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleToggleStatus = async (userId: number) => {
    setProcessing(userId);
    try {
      const res = await adminService.updateUserStatus(userId);
      // As requested: show alert for every status update
      alert(res.message || "User status updated");
      await fetchUsers();
    } catch (err: any) {
      alert(`PROTOCOL ERROR: ${err.message || 'Status update failed'}`);
    } finally {
      setProcessing(null);
    }
  };

  const handleToggleAga = async (userId: number) => {
    setProcessing(userId);
    try {
      await adminService.toggleAga(userId);
      await fetchUsers();
    } catch (err: any) {
      alert(`AGA SYNC ERROR: ${err.message || 'Toggle failed'}`);
    } finally {
      setProcessing(null);
    }
  };

  const handleVerify = async (userId: number) => {
    if (!window.confirm("PROTOCOL: Manually override identity clearance?")) return;
    setProcessing(userId);
    try {
      await adminService.verifyUser(userId);
      await fetchUsers();
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm("WARNING: Permanent record deletion. This action is irreversible.")) return;
    setProcessing(userId);
    try {
      await adminService.deleteUser(userId);
      await fetchUsers();
    } finally {
      setProcessing(null);
    }
  };

  const filteredUsers = users.filter(u => 
    u.fname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.lname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const currentItems = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) return <div className="h-96 flex items-center justify-center"><div className="w-12 h-12 border-4 border-purple-600 border-t-transparent animate-spin rounded-full"></div></div>;

  return (
    <div className="space-y-10 animate-in fade-in duration-500 pb-20">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 px-2">
        <div className="flex items-center space-x-6">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-purple-600/30">
            <svg className="w-9 h-9 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
              <path d="M12 6L7 15H9.5L12 10L14.5 15H17L12 6Z" fill="currentColor"/>
              <path d="M8 18L12 16L16 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h2 className="text-4xl font-black text-white uppercase tracking-tighter">Global Personnel</h2>
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.5em] mt-1">Central Population Database</p>
          </div>
        </div>
        
        <div className="relative max-w-lg w-full">
          <input 
            type="text" 
            placeholder="Search by Identity, Email, or Hash..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full bg-slate-900/50 border border-white/5 rounded-[2rem] px-8 py-5 text-white text-sm font-bold outline-none focus:ring-2 focus:ring-purple-600/40 transition-all placeholder-slate-800 shadow-inner"
          />
          <svg className="absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </div>
      </div>

      <div className="bg-slate-900/30 backdrop-blur-3xl rounded-[3rem] border border-white/5 overflow-hidden shadow-2xl relative">
        <div className="absolute inset-0 bg-purple-600/[0.02] pointer-events-none"></div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/80 text-slate-600 uppercase text-[9px] tracking-[0.2em] font-black border-b border-white/5">
              <tr>
                <th className="px-10 py-8">Identity Node</th>
                <th className="px-10 py-8">Security Key</th>
                <th className="px-10 py-8">Link State</th>
                <th className="px-10 py-8">Elite (AGA)</th>
                <th className="px-10 py-8">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {currentItems.map((u) => {
                const id = u.userId || u.id;
                const isPassVisible = showPassMap[id];
                const cachedPassword = passwordsCache[id];
                const isLoadingPass = processing === id && !isPassVisible;
                
                // Using activeStatus field: true/1 = active, false/0 = inactive
                const isActive = u.activeStatus == 1 || u.activeStatus === true;

                return (
                  <tr key={id} className="hover:bg-purple-600/5 transition-colors group relative">
                    <td className="px-10 py-6">
                      <div className="flex items-center space-x-5">
                        <div className="w-12 h-12 bg-slate-800 rounded-2xl flex items-center justify-center font-black text-slate-500 border border-white/5 relative shrink-0 group-hover:border-purple-500/30 transition-colors">
                          {(u.verified === 1 || u.verified === true) ? <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-slate-950 shadow-[0_0_10px_#10b981]"></div> : null}
                          {u.fname?.[0]}{u.lname?.[0]}
                        </div>
                        <div>
                          <p className="text-white font-bold text-sm tracking-tight">{u.fname} {u.lname}</p>
                          <p className="text-[9px] text-slate-600 font-black uppercase tracking-widest mt-0.5">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-10 py-6">
                      <div className="flex items-center space-x-4">
                        <span className={`text-xs font-mono tracking-[0.2em] ${isPassVisible ? 'text-purple-400 font-black' : 'text-slate-800'}`}>
                          {isLoadingPass ? (
                            <span className="animate-pulse">DECRYPTING...</span>
                          ) : (
                            isPassVisible ? (cachedPassword || u.view_pass || u.password || 'VOID') : '••••••••'
                          )}
                        </span>
                        <button 
                          onClick={() => handleTogglePassword(id)} 
                          disabled={isLoadingPass}
                          className={`transition-colors p-2 rounded-lg hover:bg-white/5 ${isPassVisible ? 'text-purple-400' : 'text-slate-700 hover:text-purple-400'}`}
                        >
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {isPassVisible ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268-2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />}
                           </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-10 py-6">
                      <div className="flex items-center space-x-4">
                        <button 
                          onClick={() => handleToggleStatus(id)}
                          disabled={processing === id}
                          className={`w-12 h-6 rounded-full p-1 transition-all duration-300 relative ${isActive ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)]' : 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.4)]'}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-all duration-300 transform ${isActive ? 'translate-x-6' : 'translate-x-0'}`}></div>
                        </button>
                        <span className={`text-[9px] font-black uppercase tracking-widest ${isActive ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                    </td>
                    <td className="px-10 py-6">
                      <button 
                        onClick={() => handleToggleAga(id)}
                        disabled={processing === id}
                        className={`transition-all duration-300 hover:scale-125 focus:outline-none ${u.aga === 1 ? 'text-amber-500 drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]' : 'text-slate-700 hover:text-slate-500'}`}
                      >
                        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-10 py-6 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        {!(u.verified === 1 || u.verified === true) && (
                          <button 
                            onClick={() => handleVerify(id)}
                            disabled={processing === id}
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[9px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-emerald-600/20 active:scale-95 disabled:opacity-30"
                          >
                            Clear Identity
                          </button>
                        )}
                        <button 
                          disabled={processing === id}
                          onClick={() => handleDeleteUser(id)}
                          className="text-slate-700 hover:text-red-500 transition-colors p-3 bg-slate-950/40 rounded-xl border border-white/5"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="px-10 py-8 bg-slate-950/40 border-t border-white/5 flex items-center justify-between">
            <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Archive Page {currentPage} of {totalPages}</span>
            <div className="flex space-x-2">
               <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="px-5 py-2.5 bg-slate-900 border border-white/5 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-white transition-colors disabled:opacity-30" disabled={currentPage === 1}>Prev</button>
               <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="px-5 py-2.5 bg-slate-900 border border-white/5 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-white transition-colors disabled:opacity-30" disabled={currentPage === totalPages}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;